/**
 * C06 — parent dashboard logic (data layer; UI in parent-dashboard-ui.ts).
 *
 * The legacy single-file dashboard (src/learn/parent-dashboard.ts) already
 * covers the 7-day summary path. This module adds the v20 capabilities the
 * spec calls out beyond what existed:
 *
 *   • buildDailyStats(progress, days)   — dense day-by-day rows for the window
 *   • build30DaySummary(progress)       — 30-day totals + averages + active days
 *   • buildWeakKPTopN(progress, n)      — weakest knowledge-points (from the
 *                                         wrong-answer mistake log, with
 *                                         studentKPs fallback for older saves)
 *
 * All functions are pure (no DOM, no IO) so they're trivially unit-testable.
 *
 * The data sources:
 *   • progress.dailyXP                       → xpGained per day      (v4+)
 *   • progress.dailyQCount                   → qCount per day        (v20+)
 *   • progress.parentSettings.dailyPlayMinutes → sessionMin per day  (v15+)
 *   • progress.mistakes                      → weak-KP top-N         (v8+)
 *   • progress.studentKPs                    → fallback weak-KP source
 *
 * UTC dates everywhere so the report is stable across DST / timezone moves
 * (matches existing dailyXP key generation in shared/progress.ts).
 */

import type { PlayerProgress, DailyStat } from '../../shared/types';
import { masteryPercent } from '../../shared/student-kp';

/**
 * Build a dense list of {@link DailyStat} rows covering the `days`-day
 * window ending at `nowISO` (inclusive). Days with no recorded activity
 * still appear with zeros so the consumer can index into them by date.
 *
 * @param progress   any loaded PlayerProgress (post-migration)
 * @param days       window length; spec calls for 7 / 30
 * @param nowISO     defaults to `new Date().toISOString()`; tests inject
 */
export function buildDailyStats(
  progress: Pick<PlayerProgress, 'dailyXP' | 'dailyQCount' | 'parentSettings'>,
  days: number,
  nowISO: string = new Date().toISOString(),
): DailyStat[] {
  if (days <= 0) return [];
  const today = nowISO.slice(0, 10);
  const xpMap = progress.dailyXP ?? {};
  const qMap = progress.dailyQCount ?? {};
  const minutesMap = progress.parentSettings?.dailyPlayMinutes ?? {};

  const rows: DailyStat[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setUTCDate(d.getUTCDate() - i);
    const key = d.toISOString().slice(0, 10);
    rows.push({
      date: key,
      qCount: qMap[key] ?? 0,
      xpGained: xpMap[key] ?? 0,
      sessionMin: minutesMap[key] ?? 0,
    });
  }
  return rows;
}

/**
 * Per-window aggregation. Returned by {@link build30DaySummary} (and the
 * weekly variant used internally). 'activeDays' = days the kid did
 * ANYTHING (answered ≥1 question OR earned XP OR played ≥1 min).
 */
export interface PeriodSummary {
  /** Inclusive YYYY-MM-DD bounds of the window. */
  dateRange: { from: string; to: string };
  /** Length of window in days (7 or 30 in practice). */
  windowDays: number;
  /** Sum of all qCount values across the window. */
  totalQuestions: number;
  /** Sum of xpGained across the window. */
  totalXP: number;
  /** Sum of sessionMin across the window. */
  totalMinutes: number;
  /** Days with ≥1 unit of activity (qCount > 0 OR xp > 0 OR min > 0). */
  activeDays: number;
  /**
   * Average questions answered per active day. 0 when activeDays is 0
   * so consumers can safely render `Math.round(avg)`. (Per-active-day
   * is more meaningful than per-calendar-day because it answers the
   * parent's real question: "on days when the kid practised, how much?")
   */
  avgQuestionsPerActiveDay: number;
  /** Average minutes played per active day. */
  avgMinutesPerActiveDay: number;
}

function summarise(rows: DailyStat[]): PeriodSummary {
  let totalQuestions = 0;
  let totalXP = 0;
  let totalMinutes = 0;
  let activeDays = 0;
  for (const r of rows) {
    totalQuestions += r.qCount;
    totalXP += r.xpGained;
    totalMinutes += r.sessionMin;
    if (r.qCount > 0 || r.xpGained > 0 || r.sessionMin > 0) activeDays += 1;
  }
  const safeActive = Math.max(1, activeDays); // guard div-by-zero
  return {
    dateRange: {
      from: rows.length > 0 ? rows[0].date : '',
      to: rows.length > 0 ? rows[rows.length - 1].date : '',
    },
    windowDays: rows.length,
    totalQuestions,
    totalXP,
    totalMinutes,
    activeDays,
    avgQuestionsPerActiveDay: activeDays === 0 ? 0 : totalQuestions / safeActive,
    avgMinutesPerActiveDay: activeDays === 0 ? 0 : totalMinutes / safeActive,
  };
}

/** 7-day window summary — convenience for the dashboard header. */
export function build7DaySummary(
  progress: Pick<PlayerProgress, 'dailyXP' | 'dailyQCount' | 'parentSettings'>,
  nowISO?: string,
): PeriodSummary {
  return summarise(buildDailyStats(progress, 7, nowISO));
}

/** 30-day window summary — drives the new C06 second tab. */
export function build30DaySummary(
  progress: Pick<PlayerProgress, 'dailyXP' | 'dailyQCount' | 'parentSettings'>,
  nowISO?: string,
): PeriodSummary {
  return summarise(buildDailyStats(progress, 30, nowISO));
}

// ── Weak-KP top-N (C06 spec: "弱點 KP top-3 from wrong answer log") ──────────

/**
 * One row of the weak-KP ranking. `wrongCount` is the number of wrong
 * answers recorded in `progress.mistakes` within the window; `mastery`
 * (0-100) is the live mastery percent from `studentKPs` so the parent
 * sees BOTH the recent error volume AND how close to mastery the kid
 * actually is. Sorted ascending by mastery first (weakest at top),
 * tiebroken by higher wrongCount.
 */
export interface WeakKPRow {
  kpId: string;
  wrongCount: number;
  /** Live mastery percent from studentKPs (0-100); 0 when no KP record. */
  mastery: number;
  /** Total attempts on this KP (cumulative, all-time). 0 when no record. */
  totalAttempts: number;
}

/**
 * Top-N weakest knowledge points over the past `windowDays`. Primary
 * signal is the wrong-answer log (because it answers "what's the kid
 * actually struggling with RIGHT NOW"); for legacy saves with empty
 * mistakes (e.g. pre-v8 imports) we fall back to ranking by
 * mastery-percent over studentKPs filtered to >=3 attempts.
 *
 * Why both sources: a kid who just opened the app today has no mistakes
 * yet, but might have a real history in studentKPs (e.g. transferred
 * from another device). The fallback keeps the parent dashboard useful
 * during that gap.
 */
export function buildWeakKPTopN(
  progress: Pick<PlayerProgress, 'mistakes' | 'studentKPs'>,
  n: number = 3,
  windowDays: number = 30,
  nowMs: number = Date.now(),
): WeakKPRow[] {
  if (n <= 0) return [];
  const kpRecords = progress.studentKPs ?? [];
  const masteryOf = (kpId: string): { mastery: number; totalAttempts: number } => {
    const rec = kpRecords.find((k) => k.kpId === kpId);
    if (!rec) return { mastery: 0, totalAttempts: 0 };
    return { mastery: masteryPercent(rec), totalAttempts: rec.totalAttempts };
  };

  const windowStartMs = nowMs - windowDays * 24 * 60 * 60 * 1000;
  const recentMistakes = (progress.mistakes ?? []).filter(
    (m) => m.timestamp >= windowStartMs,
  );

  if (recentMistakes.length > 0) {
    // Aggregate wrong-answer counts per KP within window.
    const counts = new Map<string, number>();
    for (const m of recentMistakes) {
      counts.set(m.kp, (counts.get(m.kp) ?? 0) + 1);
    }
    const rows: WeakKPRow[] = [];
    for (const [kpId, wrongCount] of counts.entries()) {
      const { mastery, totalAttempts } = masteryOf(kpId);
      rows.push({ kpId, wrongCount, mastery, totalAttempts });
    }
    // Ascending by mastery (weakest first); break ties by HIGHER wrongCount
    // so the most-troublesome KP (10 recent wrongs) outranks a quieter one
    // (2 recent wrongs) at the same mastery.
    rows.sort((a, b) => a.mastery - b.mastery || b.wrongCount - a.wrongCount);
    return rows.slice(0, n);
  }

  // Fallback path — no recent mistakes recorded; lean on studentKP mastery
  // alone, but only consider KPs with enough attempts to be meaningful
  // (mirrors the existing dashboard's >=3 filter).
  return kpRecords
    .filter((kp) => kp.totalAttempts >= 3)
    .map((kp) => ({
      kpId: kp.kpId,
      wrongCount: kp.totalAttempts - kp.correctCount,
      mastery: masteryPercent(kp),
      totalAttempts: kp.totalAttempts,
    }))
    .sort((a, b) => a.mastery - b.mastery || b.wrongCount - a.wrongCount)
    .slice(0, n);
}
